import { throttle } from 'lodash';
import { MutableRefObject, useEffect, useRef, useState } from 'react';

/**
 * This hook returns the position and dimensions of the referenced element in pixels relative to the viewport.
 */
export const useBoundingClientRect = <T extends Element>(): [
  DOMRect | null,
  MutableRefObject<T | null>,
] => {
  const ref = useRef<T>();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return _useBoundingClientRect<T>(ref);
};

/**
 * DO NOT USE: Please use useBoundingClientRect instead. This inner function is split out for ease of testing.
 */
export const _useBoundingClientRect = <T extends Element>(
  ref: MutableRefObject<T>,
): [DOMRect | null, MutableRefObject<T | null>] => {
  const [boundingClientRect, setBoundingClientRect] = useState<DOMRect | null>();

  const set = throttle(() => setBoundingClientRect(ref.current?.getBoundingClientRect()), 100);

  useEffect(() => {
    set();
    const currentRef = ref.current;
    if (currentRef == null) {
      return;
    }
    const resizeObserver = new ResizeObserver(set);
    resizeObserver.observe(currentRef);
    // scroll events must be detected by capturing as scroll events don't bubble
    window.addEventListener('scroll', set, true);
    return () => {
      resizeObserver.unobserve(currentRef);
      window.removeEventListener('scroll', set, true);
    };
  }, []);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return [boundingClientRect, ref];
};
